import type { FormEvent } from 'react'
import type { GenerateStatisticDataInputName, GenerateStatisticDataFormData } from 'types'
import type { Offer } from 'types/seller'

import { useState, useEffect } from 'react'

import { useStore } from 'store'
import { trimFormData, getInput } from 'utils'
import { validateStatisticDataForm } from 'utils/pageValidators'

import ModalContent from 'components/ModalContent'
import Button from 'components/Button'
import InputLabel from 'components/InputLabel'
import Input, { PrefixedInput } from 'components/Input'
import Radio, { RadioLabel, CombinedRadios } from 'components/Radio'
import Spinner from 'components/Spinner'
import StyledModal from 'components/layout/StyledModal'
import copy from 'copy.json'

const text = copy.seller.offer_details.modal.generate_statistic_data

type Props = {
  isModalOpen: boolean
  onModalClose: () => void
  offerId: number
  energyType: Offer['energy_type']
  refreshData: () => void
}

export default function GenerateStatisticDataModal({
  offerId,
  energyType,
  isModalOpen,
  onModalClose,
  refreshData
}: Props) {
  const [moutingPlace, setMoutingPlace] = useState<'FREE' | 'BUILDING'>('FREE')
  const [trackingType, setTrackingType] = useState<
    'FIXED' | 'VERTICAL_AXIS' | 'INCLINED_AXIS' | 'TWO_AXIS'
  >('FIXED')
  const [pvTechnology, setPvTechnology] = useState<'CRYSTALLINE_SILICON' | 'CIS' | 'CD_TE'>(
    'CRYSTALLINE_SILICON'
  )
  const { api, fieldErrors } = useStore()
  const [errors, setErrors] = useState<{ [key in GenerateStatisticDataInputName]?: string }>({})
  const [loading, setLoading] = useState(false)

  const validateInput = (
    event?: FormEvent<HTMLInputElement>,
    element?: HTMLInputElement | null
  ) => {
    const input = element || event?.currentTarget
    if (!input) return
    const validation = validateStatisticDataForm(input)
    setErrors(state => ({ ...state, [input.name]: validation }))
    return validation
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)

    const form = event.currentTarget
    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateStatisticDataForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) {
      setErrors(errors)
      setLoading(false)
      return
    }

    const requestData = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({ ...result, [name]: value }),
      {} as GenerateStatisticDataFormData
    )

    requestData.energy_type = energyType
    requestData.azimuth = Number(requestData.azimuth)
    requestData.slope = Number(requestData.slope)
    requestData.system_loss = Number(requestData.system_loss)

    await api.assignStatisticalProfile(offerId, 'SOLAR', requestData)
    const offerDetails = await api.getResource('offers', offerId)
    setLoading(false)

    if (offerDetails?.has_energy_profile) {
      refreshData()
      onModalClose()
    }
  }

  useEffect(() => {
    setErrors(fieldErrors)
    getInput(Object.keys(fieldErrors)[0])?.scrollIntoView({ behavior: 'smooth' })
  }, [fieldErrors])

  return (
    <StyledModal isOpen={isModalOpen} onClose={onModalClose}>
      <ModalContent as="form" onSubmit={submit}>
        <h3>{text.title}</h3>

        <InputLabel>
          <span>{text.form.azimuth}</span>
          <span>{errors.azimuth}</span>
          <Input
            name="azimuth"
            type="number"
            defaultValue={180}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{text.form.slope}</span>
          <span>{errors.slope}</span>
          <PrefixedInput
            name="slope"
            type="number"
            defaultValue={30}
            prefix={text.form.degrees}
            prefixWidth="50px"
            reverse
            step={1}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <CombinedRadios>
          <span>{text.form.mouting_place}</span>
          <span>{errors.mouting_place}</span>
          <RadioLabel>
            <Radio
              name="mouting_place"
              value="FREE"
              checked={moutingPlace === 'FREE'}
              onChange={() => setMoutingPlace('FREE')}
            />
            {text.form.free}
          </RadioLabel>

          <RadioLabel>
            <Radio
              name="mouting_place"
              value="BUILDING"
              checked={moutingPlace === 'BUILDING'}
              onChange={() => setMoutingPlace('BUILDING')}
            />
            {text.form.building}
          </RadioLabel>
        </CombinedRadios>

        <CombinedRadios>
          <span>{text.form.tracking_type}</span>
          <span>{errors.tracking_type}</span>
          <RadioLabel>
            <Radio
              name="tracking_type"
              value="FIXED"
              checked={trackingType === 'FIXED'}
              onChange={() => setTrackingType('FIXED')}
            />
            {text.form.fixed}
          </RadioLabel>

          <RadioLabel>
            <Radio
              name="tracking_type"
              value="VERTICAL_AXIS"
              checked={trackingType === 'VERTICAL_AXIS'}
              onChange={() => setTrackingType('VERTICAL_AXIS')}
            />
            {text.form.vertical_axis}
          </RadioLabel>

          <RadioLabel>
            <Radio
              name="tracking_type"
              value="INCLINED_AXIS"
              checked={trackingType === 'INCLINED_AXIS'}
              onChange={() => setTrackingType('INCLINED_AXIS')}
            />
            {text.form.inclined_axis}
          </RadioLabel>

          <RadioLabel>
            <Radio
              name="tracking_type"
              value="TWO_AXIS"
              checked={trackingType === 'TWO_AXIS'}
              onChange={() => setTrackingType('TWO_AXIS')}
            />
            {text.form.two_axis}
          </RadioLabel>
        </CombinedRadios>

        <CombinedRadios>
          <span>{text.form.pv_technology}</span>
          <span>{errors.pv_technology}</span>
          <RadioLabel>
            <Radio
              name="pv_technology"
              value="CRYSTALLINE_SILICON"
              checked={pvTechnology === 'CRYSTALLINE_SILICON'}
              onChange={() => setPvTechnology('CRYSTALLINE_SILICON')}
            />
            {text.form.crystalline_silicon}
          </RadioLabel>

          <RadioLabel>
            <Radio
              name="pv_technology"
              value="CIS"
              checked={pvTechnology === 'CIS'}
              onChange={() => setPvTechnology('CIS')}
            />
            {text.form.cis}
          </RadioLabel>

          <RadioLabel>
            <Radio
              name="pv_technology"
              value="CD_TE"
              checked={pvTechnology === 'CD_TE'}
              onChange={() => setPvTechnology('CD_TE')}
            />
            {text.form.cd_te}
          </RadioLabel>
        </CombinedRadios>

        <InputLabel>
          <span>{text.form.system_loss}</span>
          <span>{errors.system_loss}</span>
          <PrefixedInput
            name="system_loss"
            type="number"
            defaultValue={14}
            prefix="%"
            prefixWidth="50px"
            reverse
            step={1}
            min={0}
            max={100}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <Button type="submit">{loading ? <Spinner size={16} /> : text.form.submit_button}</Button>

        <Button type="button" secondary onClick={onModalClose}>
          {text.form.cancel_button}
        </Button>
      </ModalContent>
    </StyledModal>
  )
}
